import React, { useState, useEffect, useCallback } from "react";
import { Card, Tabs, Space, Typography, Button, Badge, message, Row, Col, Divider } from "antd";
import { UserOutlined, SafetyOutlined, ReloadOutlined, HistoryOutlined } from "@ant-design/icons";
import { get_activity_logsApi } from "../../api/admin.apis";
import StatsCards from "./StatsCards";
import FilterPanel from "./FilterPanel";
import LogsTable from "./LogsTable";
import LogDetailModal from "./LogDetailModal";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const ActivityLogs = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("regular");
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [allLogs, setAllLogs] = useState([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const [filters, setFilters] = useState({
    search: "",
    action: null,
    method: null,
    status: null,
  });

  const fetchLogs = useCallback(async (page = 1, pageSize = 20) => {
    try {
      setLoading(true);

      const params = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };

      const response = await get_activity_logsApi(params);

      if (response?.data?.status === "success") {
        const apiLogs = response.data.data || [];
        setAllLogs(apiLogs);

        const filteredByTab =
          activeTab === "impersonated"
            ? apiLogs.filter((log) => log.impersonated === true)
            : apiLogs.filter((log) => log.impersonated !== true);

        setLogs(filteredByTab);

        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize,
          total: response.data.meta?.total || 0,
        }));
      }
    } catch (error) {
      message.error("Failed to fetch activity logs");
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchLogs(1, pagination.pageSize);
  }, [activeTab]);

  const handleTableChange = (newPagination) => {
    fetchLogs(newPagination.current, newPagination.pageSize);
  };

  const regularCount = allLogs.filter(l => l.impersonated !== true).length;
  const impersonatedCount = allLogs.filter(l => l.impersonated === true).length;

  return (
    <div style={{ padding: 24, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>

        <Card bordered={false} style={{ borderRadius: 12 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space size="middle">
                <HistoryOutlined style={{ fontSize: 24 }} />
                <div>
                  <Title level={3} style={{ margin: 0 }}>Activity Logs</Title>
                  <Text type="secondary">
                    Monitor and audit all system activities in real-time
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={() => fetchLogs(pagination.current, pagination.pageSize)}
              >
                Refresh
              </Button>
            </Col>
          </Row>
        </Card>

        <StatsCards logs={logs} />

        <FilterPanel filters={filters} setFilters={setFilters} />

        <Card bordered={false} style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
          >
            <TabPane
              tab={
                <Space>
                  <UserOutlined />
                  Regular Activity
                  <Badge count={regularCount} />
                </Space>
              }
              key="regular"
            >
              <Divider style={{ margin: 0 }} />
              <LogsTable
                logs={logs}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                onView={(record) => {
                  setSelectedLog(record);
                  setDetailModalVisible(true);
                }}
              />
            </TabPane>

            <TabPane
              tab={
                <Space>
                  <SafetyOutlined />
                  Impersonated Activity
                  <Badge count={impersonatedCount} />
                </Space>
              }
              key="impersonated"
            >
              <Divider style={{ margin: 0 }} />
              <LogsTable
                logs={logs}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                onView={(record) => {
                  setSelectedLog(record);
                  setDetailModalVisible(true);
                }}
              />
            </TabPane>
          </Tabs>
        </Card>

        <LogDetailModal
          visible={detailModalVisible}
          log={selectedLog}
          onClose={() => setDetailModalVisible(false)}
        />
      </Space>
    </div>
  );
};

export default ActivityLogs;
